import React, { useState, useMemo } from "react";
import { toast } from "sonner";
import Papa from "papaparse";
import api from "@/lib/api";
import { X, Upload, Printer, Layers, Calendar, Hash, Bell, Mail, Phone } from "lucide-react";

function parseCSV(text) {
  if (!text.trim()) return [];
  const res = Papa.parse(text.trim(), {
    header: false,
    skipEmptyLines: true,
  });
  const rows = res.data;
  if (!rows.length) return [];
  // Detect header (first row contains 'code' or 'label' literally)
  let codeIdx = 0;
  let labelIdx = 1;
  let emailIdx = 2;
  let phoneIdx = 3;
  let startIdx = 0;
  const firstLower = (rows[0] || []).map((c) => (c || "").toString().toLowerCase().trim());
  if (firstLower.includes("code") || firstLower.includes("label")) {
    const find = (...names) => firstLower.findIndex((h) => names.includes(h));
    codeIdx = firstLower.indexOf("code");
    labelIdx = firstLower.indexOf("label");
    if (codeIdx === -1) codeIdx = 0;
    if (labelIdx === -1) labelIdx = codeIdx === 0 ? 1 : 0;
    emailIdx = find("owner_email", "email", "owner email");
    phoneIdx = find("owner_phone", "phone", "owner phone");
    startIdx = 1;
  }
  const cell = (row, idx) => (idx >= 0 ? ((row[idx] || "") + "").trim() : "");
  const items = [];
  for (let i = startIdx; i < rows.length; i++) {
    const row = rows[i] || [];
    const code = cell(row, codeIdx).toUpperCase();
    const label = cell(row, labelIdx);
    if (!code && !label) continue;
    items.push({ code, label, owner_email: cell(row, emailIdx) || null, owner_phone: cell(row, phoneIdx) || null });
  }
  return items;
}

export default function LotDropModal({ files, onClose, onDone }) {
  const [csvText, setCsvText] = useState("code,label,owner_email,owner_phone\nLOT-001,Bottle #001,owner1@example.com,+351912345678\nLOT-002,Bottle #002,,\nLOT-003,Bottle #003,,");
  const [fileId, setFileId] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [maxUses, setMaxUses] = useState("");
  const [notifyEmail, setNotifyEmail] = useState("");
  const [lotOwnerEmail, setLotOwnerEmail] = useState("");
  const [lotOwnerPhone, setLotOwnerPhone] = useState("");
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const items = useMemo(() => parseCSV(csvText), [csvText]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const text = await f.text();
    setCsvText(text);
    e.target.value = "";
  };

  const submit = async () => {
    if (items.length === 0) {
      toast.error("No items to create");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/codes/bulk", {
        items,
        file_id: fileId || null,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
        max_uses: maxUses ? parseInt(maxUses, 10) : null,
        notify_email: notifyEmail.trim() || null,
        owner_email: lotOwnerEmail.trim() || null,
        owner_phone: lotOwnerPhone.trim() || null,
        skip_duplicates: skipDuplicates,
      });
      const createdIds = data.created.map((c) => c.id).join(",");
      const skipped = data.skipped?.length || 0;
      toast.success(`${data.created.length} codes created${skipped ? ` · ${skipped} skipped` : ""}`);
      (data.skipped || []).filter((s) => s.reason !== "duplicate").slice(0, 3).forEach((s) => toast.error(`${s.code}: ${s.reason}`));
      onDone?.();
      if (data.created.length > 0) {
        // Open printable sheet in new tab
        window.open(`/admin/sheet?ids=${createdIds}`, "_blank");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk create failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.75)" }}
      data-testid="lot-drop-modal"
      onClick={onClose}
    >
      <div
        className="glass rounded-2xl p-8 max-w-4xl w-full fade-up max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="overline text-[var(--vault-text-muted)] inline-flex items-center gap-1.5">
              <Layers className="w-3 h-3" /> Bulk action
            </p>
            <h3 className="font-display text-3xl mt-1">Lot Drop</h3>
            <p className="text-sm text-[var(--vault-text-muted)] mt-1">
              Generate many codes at once from a CSV. Each gets the same file, expiration and notification settings.
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/5" data-testid="lot-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: CSV input */}
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="overline text-[var(--vault-text-muted)]">CSV (code, label, owner_email, owner_phone)</label>
                <label className="overline text-[var(--vault-gold)] cursor-pointer inline-flex items-center gap-1.5 hover:underline">
                  <Upload className="w-3 h-3" /> Upload .csv
                  <input type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" data-testid="lot-csv-upload" />
                </label>
              </div>
              <textarea
                data-testid="lot-csv-text"
                value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
                rows={12}
                className="w-full px-3 py-3 rounded font-mono-vault text-xs outline-none resize-y"
                style={{ background: "var(--vault-bg)", border: "1px solid var(--vault-border)" }}
              />
              <p className="text-[10px] text-[var(--vault-text-faint)] mt-1">
                Header optional. Format: <span className="font-mono-vault">code,label,owner_email,owner_phone</span>. Leave code blank to auto-generate. Rows without an owner use the lot owner.
              </p>
            </div>

            <label className="flex items-center gap-2 text-sm text-[var(--vault-text-muted)] cursor-pointer">
              <input
                type="checkbox"
                checked={skipDuplicates}
                onChange={(e) => setSkipDuplicates(e.target.checked)}
                data-testid="lot-skip-dup"
                className="accent-[var(--vault-gold)]"
              />
              Skip duplicates silently
            </label>
          </div>

          {/* Right: shared settings + preview */}
          <div className="space-y-4">
            <div>
              <label className="overline block text-[var(--vault-text-muted)] mb-2">Linked file (shared)</label>
              <select
                data-testid="lot-file-select"
                value={fileId}
                onChange={(e) => setFileId(e.target.value)}
                className="w-full px-3 py-2.5 rounded outline-none text-sm"
                style={{ background: "var(--vault-bg)", border: "1px solid var(--vault-border)" }}
              >
                <option value="">— Assign later —</option>
                {files.map((f) => (
                  <option key={f.id} value={f.id}>{f.original_filename}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline block text-[var(--vault-text-muted)] mb-2 inline-flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Expires
                </label>
                <input
                  type="datetime-local"
                  data-testid="lot-expires"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  className="w-full px-3 py-2.5 rounded outline-none text-sm"
                  style={{ background: "var(--vault-bg)", border: "1px solid var(--vault-border)", colorScheme: "dark" }}
                />
              </div>
              <div>
                <label className="overline block text-[var(--vault-text-muted)] mb-2 inline-flex items-center gap-1">
                  <Hash className="w-3 h-3" /> Max uses
                </label>
                <input
                  type="number"
                  min="0"
                  data-testid="lot-max-uses"
                  value={maxUses}
                  onChange={(e) => setMaxUses(e.target.value)}
                  placeholder="Unlimited"
                  className="w-full px-3 py-2.5 rounded outline-none text-sm"
                  style={{ background: "var(--vault-bg)", border: "1px solid var(--vault-border)" }}
                />
              </div>
            </div>
            <div>
              <label className="overline block text-[var(--vault-text-muted)] mb-2 inline-flex items-center gap-1">
                <Bell className="w-3 h-3" /> Notify email (shared)
              </label>
              <input
                type="email"
                data-testid="lot-notify"
                value={notifyEmail}
                onChange={(e) => setNotifyEmail(e.target.value)}
                placeholder="you@verifinewines.com"
                className="w-full px-3 py-2.5 rounded outline-none text-sm"
                style={{ background: "var(--vault-bg)", border: "1px solid var(--vault-border)" }}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline block text-[var(--vault-text-muted)] mb-2 inline-flex items-center gap-1"><Mail className="w-3 h-3" /> Lot owner email</label>
                <input type="email" data-testid="lot-owner-email" value={lotOwnerEmail} onChange={(e) => setLotOwnerEmail(e.target.value)} placeholder="Default for empty rows" className="w-full px-3 py-2.5 rounded outline-none text-sm" style={{ background: "var(--vault-bg)", border: "1px solid var(--vault-border)" }} />
              </div>
              <div>
                <label className="overline block text-[var(--vault-text-muted)] mb-2 inline-flex items-center gap-1"><Phone className="w-3 h-3" /> Lot owner phone</label>
                <input type="tel" data-testid="lot-owner-phone" value={lotOwnerPhone} onChange={(e) => setLotOwnerPhone(e.target.value)} placeholder="+351 912 345 678" className="w-full px-3 py-2.5 rounded outline-none text-sm" style={{ background: "var(--vault-bg)", border: "1px solid var(--vault-border)" }} />
              </div>
            </div>

            <div
              className="rounded-lg p-4"
              style={{ background: "var(--vault-bg)", border: "1px solid var(--vault-border)" }}
            >
              <p className="overline text-[var(--vault-text-muted)] mb-2">Preview · {items.length} codes</p>
              <div className="max-h-40 overflow-auto text-xs font-mono-vault space-y-1">
                {items.slice(0, 20).map((it, i) => (
                  <div key={`${it.code || "auto"}-${it.label}-${i}`} className="flex gap-3">
                    <span className="text-[var(--vault-gold)] w-24">{it.code || "<auto>"}</span>
                    <span className="text-[var(--vault-text-muted)] truncate flex-1">{it.label}</span>
                    <span className="text-[var(--vault-text-faint)] truncate max-w-[45%]" data-testid={`lot-preview-owner-${i}`}>{it.owner_email || lotOwnerEmail || "—"}</span>
                  </div>
                ))}
                {items.length > 20 && (
                  <p className="text-[var(--vault-text-faint)] pt-1">…and {items.length - 20} more</p>
                )}
                {items.length === 0 && (
                  <p className="text-[var(--vault-text-faint)]">No valid rows yet</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-6 mt-6 border-t" style={{ borderColor: "var(--vault-border)" }}>
          <button type="button" onClick={onClose} className="btn-ghost px-4 py-2 rounded-md text-sm">Cancel</button>
          <button
            onClick={submit}
            disabled={submitting || items.length === 0}
            data-testid="lot-submit"
            className="btn-gold px-5 py-2 rounded-md text-sm inline-flex items-center gap-2"
          >
            {submitting ? <span className="spin" /> : (
              <>
                <Printer className="w-4 h-4" />
                Create &amp; Print {items.length || ""} QR{items.length === 1 ? "" : "s"}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
